import { ApiResponse, CrawledPages, WebSource, WebSourceConfig } from '../types';
import { kbApi } from './api';

/**
 * Web sources (the crawler) for a chatbot's knowledge base.
 *
 * Backed by `kb-web-source` in stacks/kb:
 *
 *   GET    /chatbots/{id}/knowledge-base/websites
 *   POST   /chatbots/{id}/knowledge-base/websites          body: WebSourceConfig
 *   POST   /chatbots/{id}/knowledge-base/websites/crawl    body: { websiteIds }
 *   DELETE /chatbots/{id}/knowledge-base/websites/{siteId}
 *   POST   /chatbots/{id}/knowledge-base/websites/{siteId}/recrawl
 *
 * Each website is ONE Bedrock data source on the chatbot's managed knowledge
 * base, produced by the `WEB` connector — not one row per URL. Adding stages
 * only: the crawl begins when the user commits with Update knowledge, which is
 * the /crawl call.
 *
 * Websites require a MANAGED knowledge base. A chatbot still on s3_vectors or
 * opensearch has no second data source to attach, and the API answers 400.
 */

const STORAGE_KEY = 'celsian.webSources';

/**
 * Scope choice -> the connector's crawl configuration.
 *
 * These names were measured against the live connector, not read off the SDK:
 * unknown keys in connectorParameters are accepted, stored, echoed back by
 * GetDataSource and then silently ignored, so a wrong name produces a crawl
 * that quietly runs with defaults and never errors. `HOST_ONLY`/`SUBDOMAINS`
 * belong to the SELF-MANAGED web data source and are rejected here; this
 * connector's enum is [PATH_SPECIFIC, SUB_DOMAINS, ALL_DOMAINS, DOMAINS_ONLY].
 * The translation itself lives in the Lambda — see build_connector_parameters.
 */
export const SCOPE_TO_CRAWLER_CONFIG: Record<string, string> = {
  page: 'crawlDepth: 0',
  section: 'syncScope: PATH_SPECIFIC (same host and path — the connector default)',
  host: 'syncScope: DOMAINS_ONLY',
  subdomains: 'syncScope: SUB_DOMAINS',
};

/**
 * Turn a plain user pattern into the regex Bedrock's inclusion/exclusion
 * filters expect. Users type `/blog/` or `*.pdf`; they never see a regex box.
 * A pattern with no `*` is treated as "URL contains this".
 *
 * The Lambda converts too, and deliberately: this copy is for previewing the
 * result in the dialog, and the server cannot trust a browser anyway.
 */
export function patternToRegex(pattern: string): string {
  const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
  const expanded = escaped.replace(/\*/g, '.*');
  return pattern.includes('*') ? expanded : `.*${expanded}.*`;
}

/** Host shown as the source's name in the list. Falls back to the raw string. */
export function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/**
 * Bedrock's crawler will not follow these, so reject them at the door rather
 * than letting a data source fail asynchronously minutes later. The Lambda
 * repeats every one of these checks — this copy only saves a round trip.
 */
export function validateCrawlUrl(raw: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return 'Enter a full URL, including https://';
  }
  // HTTPS only, despite the docs saying either scheme works: the connector
  // validates seedUrls against a regex that hard-requires https://, and an
  // http:// URL is accepted at creation and then fails the data source
  // asynchronously — the delayed opaque failure this check exists to prevent.
  if (parsed.protocol === 'http:') {
    return 'Only https:// addresses can be crawled';
  }
  if (parsed.protocol !== 'https:') {
    return 'Enter a full URL, including https://';
  }
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(parsed.hostname)) {
    return 'IP addresses cannot be crawled — use a domain name';
  }
  if (parsed.username || parsed.password) {
    return 'Pages that require a sign-in cannot be crawled';
  }
  return null;
}

/**
 * Drop the mock's leftovers.
 *
 * Before the backend existed this module persisted fake websites to
 * localStorage. Those entries have ids no data source was ever created for, so
 * left in place they would show alongside real ones and fail every action.
 * Cleared once, on first load.
 */
function clearMockResidue(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private mode or blocked storage — nothing to clean up in that case.
  }
}

clearMockResidue();

/** Axios rejects on non-2xx, so every call funnels through here. */
function toError(e: any, fallback: string): ApiResponse<any> {
  const error = e?.response?.data?.error || e?.message || fallback;
  return { success: false, error };
}

export const webSourceApi = {
  async list(chatbotId: string): Promise<ApiResponse<{ websites: WebSource[] }>> {
    try {
      const response = await kbApi.get(`/chatbots/${chatbotId}/knowledge-base/websites`);
      return response.data;
    } catch (e) {
      return toError(e, 'Could not load websites');
    }
  },

  async add(chatbotId: string, cfg: WebSourceConfig): Promise<ApiResponse<WebSource>> {
    try {
      const response = await kbApi.post(
        `/chatbots/${chatbotId}/knowledge-base/websites`,
        cfg
      );
      return response.data;
    } catch (e) {
      return toError(e, 'Could not add that website');
    }
  },

  /**
   * Commit step: begin reading every site still waiting.
   *
   * Creating each data source is synchronous server-side (a few seconds, polled
   * to AVAILABLE because a successful create proves nothing for this connector),
   * but the crawl itself is not waited on — sites come back as `crawling` and
   * `list` advances them as their ingestion jobs finish.
   */
  async startCrawl(chatbotId: string, websiteIds: string[]): Promise<ApiResponse<void>> {
    try {
      const response = await kbApi.post(
        `/chatbots/${chatbotId}/knowledge-base/websites/crawl`,
        { websiteIds }
      );
      return response.data;
    } catch (e) {
      return toError(e, 'Could not start reading those websites');
    }
  },

  /**
   * The pages one crawl actually indexed.
   *
   * Reconstructed server-side from per-document ingestion logs, because Bedrock has no
   * API that will enumerate a web data source. Those logs expire after 30 days, so a
   * site crawled longer ago than that legitimately returns nothing — `reason` says
   * which case it is, and the UI must not render an empty list as "indexed nothing".
   */
  async listPages(chatbotId: string, websiteId: string): Promise<ApiResponse<CrawledPages>> {
    try {
      const response = await kbApi.get(
        `/chatbots/${chatbotId}/knowledge-base/websites/${websiteId}/pages`
      );
      return response.data;
    } catch (e) {
      return toError(e, 'Could not load the page list');
    }
  },

  async remove(chatbotId: string, websiteId: string): Promise<ApiResponse<void>> {
    try {
      const response = await kbApi.delete(
        `/chatbots/${chatbotId}/knowledge-base/websites/${websiteId}`
      );
      return response.data;
    } catch (e) {
      return toError(e, 'Could not remove that website');
    }
  },

  async recrawl(chatbotId: string, websiteId: string): Promise<ApiResponse<WebSource>> {
    try {
      const response = await kbApi.post(
        `/chatbots/${chatbotId}/knowledge-base/websites/${websiteId}/recrawl`
      );
      return response.data;
    } catch (e) {
      return toError(e, 'Could not start the recrawl');
    }
  },
};
